import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import {
    AbstractControl,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
    ValidationErrors,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ProductRegistrationOverviewDto, WarrantyCancelModalResponse } from '@warema/shared/models';
import { PopupContent, WaremaButton, WaremaFieldLabel, WaremaInputField } from '@warema/warema-ui-angular';

export type CancelWarrantyPopupProduct = Pick<
    ProductRegistrationOverviewDto,
    'id' | 'itemNo' | 'productId' | 'contractEndDate' | 'createDate'
>;

@Component({
    selector: 'warema-cancel-warranty-popup',
    templateUrl: './cancel-warranty-popup.component.html',
    styleUrls: ['./cancel-warranty-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, DatePipe, WaremaButton, WaremaInputField, WaremaFieldLabel, ReactiveFormsModule],
})
export class CancelWarrantyPopupComponent extends PopupContent<WarrantyCancelModalResponse> {
    private readonly _fb = inject(UntypedFormBuilder);

    public readonly product = input.required<CancelWarrantyPopupProduct>();

    public cancellationReasonMaxLength = 250;
    public form: UntypedFormGroup;

    constructor() {
        super();
        this.form = this._fb.group({
            cancellationReason: [
                '',
                [
                    (c: AbstractControl): ValidationErrors | null => Validators.required(c),
                    Validators.minLength(3),
                    Validators.maxLength(this.cancellationReasonMaxLength),
                ],
            ],
        });
    }

    public confirm(): void {
        const cancellationReason = this.form.get('cancellationReason')?.value as string;
        this.popupResult$?.next({
            confirmed: true,
            cancellationReason,
            id: this.product().id,
            isWithinRevocationPeriod: this.isWithinRevocationPeriod(this.product()),
        });
    }

    public cancel(): void {
        this.popupResult$.next({ confirmed: false });
    }

    public isWithinRevocationPeriod(product: CancelWarrantyPopupProduct): boolean {
        if (product.createDate === null) {
            return false;
        }

        // create a new date object to avoid mutating the original createDate
        const revocationEnd = new Date(product.createDate);
        revocationEnd.setDate(revocationEnd.getDate() + 14);
        revocationEnd.setHours(0, 0, 0, 0);

        return new Date() <= revocationEnd;
    }

    public getEndDate(product: CancelWarrantyPopupProduct): Date {
        return this.isWithinRevocationPeriod(product) ? new Date() : product.contractEndDate;
    }
}
